/**
 * WhatsApp Utilities
 * Functions for building WhatsApp links and messages
 */

import { PHONE_DEFAULTS } from "./constants";

/**
 * Convert phone number to E.164 format (digits only, with country code)
 * @param {string} phone - Phone number in any format
 * @param {string} defaultCountryCode - Default country code (e.g., "+34")
 * @returns {string} Phone in E.164 format (digits only)
 */
export function toE164Digits(phone, defaultCountryCode = PHONE_DEFAULTS.COUNTRY_CODE) {
  if (!phone) return "";
  
  let cleaned = String(phone).trim();
  
  // Convert 00XX to +XX
  if (cleaned.startsWith("00")) {
    cleaned = "+" + cleaned.slice(2);
  }
  
  // Remove all non-digit characters except +
  cleaned = cleaned.replace(/[^\d+]/g, "");
  
  // Add default country code if not present
  if (!cleaned.startsWith("+")) {
    cleaned = defaultCountryCode + cleaned;
  }
  
  // Remove + for final E.164 digits format
  return cleaned.replace(/\D/g, "");
}

/**
 * Build WhatsApp web link
 * @param {Object} params
 * @param {string} params.phone - Phone number (optional, if null opens contact picker)
 * @param {string} params.text - Message text
 * @returns {string} WhatsApp web URL
 */
export function buildWhatsAppLink({ phone = null, text = "" }) {
  const baseUrl = phone 
    ? `https://wa.me/${toE164Digits(phone)}`
    : "https://wa.me/";
  
  if (!text) return baseUrl;
  
  return `${baseUrl}?text=${encodeURIComponent(text)}`;
}

/**
 * Build WhatsApp message for signature invitation
 * @param {Object} params
 * @param {string} params.link - Signature link
 * @param {string} params.documentName - Document name (optional)
 * @returns {string} WhatsApp message text
 */
export function buildInvitationMessage({ link, documentName = "documento" }) {
  return `Hola, por favor firma este ${documentName} para confirmar la entrega.

Enlace: ${link}

Gracias.`;
}

/**
 * Build WhatsApp message for signed document
 * @param {Object} params
 * @param {string} params.downloadUrl - Download URL
 * @param {string} params.documentName - Document name (optional)
 * @returns {string} WhatsApp message text
 */
export function buildSignedDocumentMessage({ downloadUrl, documentName = "documento" }) {
  return `Aquí tienes el ${documentName} firmado: ${downloadUrl}`;
}

/**
 * Open WhatsApp in new window/tab
 * @param {string} url - WhatsApp URL
 */
export function openWhatsApp(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Share via WhatsApp (uses native share if available)
 * @param {string} text - Text to share
 * @param {string} fallbackUrl - Fallback WhatsApp URL
 */
export async function shareViaWhatsApp(text, fallbackUrl) {
  // Try native share first (mobile)
  if (navigator.share) {
    try {
      await navigator.share({ text });
      return;
    } catch (err) {
      // User cancelled or share not available, fall through
    }
  }
  
  // Fallback to WhatsApp web
  openWhatsApp(fallbackUrl);
}
